import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { articles, categories } from '@/mocks/articles';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import JsonLd from '@/components/base/JsonLd';
import { useSEO } from '@/hooks/useSEO';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.categorySlug === slug);

  const seoTitle = category
    ? `${category.name} - 《炽热之夜》菲律宾电影 | 炽热之夜电影资讯站`
    : '分类未找到 | 炽热之夜电影资讯站';
  const seoDesc = category
    ? `${category.description}。浏览《炽热之夜》的${category.name}相关文章，深入了解这部菲律宾情色剧情电影。`
    : '浏览《炽热之夜》菲律宾电影分类文章';

  useSEO({
    title: seoTitle,
    description: seoDesc,
    keywords: `炽热之夜${category?.name ?? ''},菲律宾电影,炽热之夜电影`,
    canonicalUrl: `https://chirezhizhiye.ph/category/${slug}`,
    ogType: 'website',
  });

  if (!category) {
    return (
      <div className="w-full px-4 md:px-6 py-20 text-center">
        <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-3">分类未找到</h2>
        <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">
          返回首页
        </Link>
      </div>
    );
  }

  const webPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 炽热之夜电影资讯站`,
    description: category.description,
    url: `https://chirezhizhiye.ph/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '炽热之夜电影资讯站',
      url: 'https://chirezhizhiye.ph',
    },
  };

  return (
    <>
      <JsonLd data={webPageJsonLd} />

      <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />

          <div className="mt-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                <i className={`${category.icon} text-lg`}></i>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                {category.name}
              </h1>
            </div>
            <p className="text-foreground-500 ml-13">{category.description}</p>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 mb-6 flex-wrap">
            <span className="text-sm text-foreground-500 whitespace-nowrap">分类导航：</span>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {categoryArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                <i className="ri-file-list-3-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">该分类暂无文章</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}